import React, { useCallback, useMemo, useState } from 'react';

import { Divider, IconButton, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { publish } from 'pubsub-js';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import WorkplaceDocumentDialog from 'components/_dialogs/WorkplaceDocumentDialog/WorkplaceDocumentDialog';
import ColoredButton from 'components/ColoredButton';
import FooterButton from 'components/FooterButton';
import TrimHTMLText from 'components/TrimHTMLText/TrimHTMLText';
import TypographyWithHTML from 'components/TypographyWithHTML/TypographyWithHTML';
import workplaceDocumentsApi, { legacyWorkplaceDocumentsApi } from 'config/api/workplaceDocuments/workplaceDocuments';
import { WorkplaceDocument } from 'config/api/workplaceDocuments/workplaceDocuments.types';
import ROLES from 'config/constants/ROLES';
import EVENTS from 'config/events/pubsub';
import downloadFromUrl from 'helpers/downloadFromUrl/downloadFromUrl';
import usePagination from 'hooks/usePagination';
import usePermissions from 'hooks/usePermissions';
import useSubscription from 'hooks/useSubscription';
import general_messages from 'messages/general_messages';
import workplace_messages from 'messages/workplace_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';

import useStyles from './WorkplacesDocumentsTable.styles';

const WorkplacesDocumentsTable: React.FC = () => {
  const { t } = useTranslation();
  const [isAccountOwner] = usePermissions([ROLES.ORGANIZATION.ACCOUNT_OWNER]);
  // @ts-ignore
  const { showConfirmationModal } = useConfirmationModalContext();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: tableData,
    getData: getTableData,
    hasNextPage,
    loading,
    refreshData,
  } = usePagination({
    endpointFunc: legacyWorkplaceDocumentsApi.getAllWorkplaceDocuments,
    limits: [5, 5, 5],
    dependencies: [],
    isGet: true,
  });

  useSubscription(EVENTS.WORKPLACE_DOCUMENTS_UPDATED, refreshData);

  const [documentDialog, setDocumentDialog] = useState<{ id: number | null; open: boolean }>({ id: null, open: false });
  const closeDocumentDialog = useCallback(() => setDocumentDialog({ id: null, open: false }), [setDocumentDialog]);

  const onOpenDocumentDialog = (id: number | null) => () => {
    setDocumentDialog({ id, open: true });
  };

  const onDeleteWorkplace = (workplaceDocument: WorkplaceDocument) => async () => {
    const confirmation = await showConfirmationModal({
      title: t(workplace_messages.remove_document.confirmation_title),
      body: t(workplace_messages.remove_document.confirmation_body, { documentName: workplaceDocument.attachment.name }),
    });
    if (confirmation) {
      try {
        await workplaceDocumentsApi.removeWorkplaceDocument(workplaceDocument.id);
        publish(EVENTS.WORKPLACE_DOCUMENTS_UPDATED);
      } catch (e) {
        enqueueSnackbar(t(general_messages.something_went_wrong), { variant: 'error' });
      }
    }
  };

  const onDownloadFactory = (url: string, name: string) => async (event: React.MouseEvent) => {
    event.preventDefault();
    await downloadFromUrl(url, name);
  };

  const styles = useStyles();
  const documents = useMemo(() => (tableData || []) as WorkplaceDocument[], [tableData]);

  return (
    <div className={styles.root}>
      <TypographyWithHTML>{t(workplace_messages.documents.description)}</TypographyWithHTML>
      {isAccountOwner && (
        // @ts-ignore
        <ColoredButton className={styles.button} customColor='secondary' onClick={onOpenDocumentDialog(null)} variant='outlined'>
          {t(workplace_messages.documents.button_add_document)}
        </ColoredButton>
      )}
      <div className={styles.filesSection}>
        {documents.length ? (
          documents.map(document => {
            const { id, attachment, description } = document;
            return (
              <div key={id}>
                <div className={styles.documentRow}>
                  <div className={styles.documentInfo}>
                    <Typography className={styles.documentName}>{attachment.name}</Typography>
                    <TrimHTMLText linesToShow={2}>
                      <TypographyWithHTML className={styles.documentDescription} style={{ whiteSpace: 'pre-line' }}>
                        {description}
                      </TypographyWithHTML>
                    </TrimHTMLText>
                  </div>
                  <div className={styles.documentActions}>
                    <IconButton onClick={onDownloadFactory(attachment.url, attachment.name)} size='small'>
                      <DownloadIcon />
                    </IconButton>
                    {isAccountOwner && (
                      <>
                        <IconButton onClick={onOpenDocumentDialog(id)} size='small'>
                          <PenIcon />
                        </IconButton>
                        <IconButton onClick={onDeleteWorkplace(document)} size='small'>
                          <TrashIcon />
                        </IconButton>
                      </>
                    )}
                  </div>
                </div>
                <Divider />
              </div>
            );
          })
        ) : (
          <Typography className={styles.emptyState}>{t(general_messages.no_data[0], general_messages.no_data[1])}</Typography>
        )}
      </div>
      {hasNextPage && (
        // @ts-ignore
        <FooterButton loading={loading} onClick={getTableData}>
          {t(general_messages.show_more)}
        </FooterButton>
      )}
      {documentDialog.open && (
        <WorkplaceDocumentDialog onClose={closeDocumentDialog} open={documentDialog.open} workplaceDocumentId={documentDialog.id} />
      )}
    </div>
  );
};

export default WorkplacesDocumentsTable;
